import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { CheckCircle } from 'lucide-react'; 
import '../styles/BillingScreen.css'; 
// --- 1. IMPORT THE NEW MODAL COMPONENT ---
import RecordPaymentModal from '../components/billing/RecordPaymentModal';

const BillingScreen = ({ user }) => {
    const [invoices, setInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, selectedInvoice: null });

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            const allInvoices = await api.getInvoices();
            setInvoices(allInvoices);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user.role === 'SuperAdmin') {
            fetchInvoices();
        } else {
            setIsLoading(false);
        }
    }, [user.role]);

    const handleRecordPayment = async (formData) => {
        const { selectedInvoice } = modalState;
        await api.recordInvoicePayment(selectedInvoice.id, formData);
        fetchInvoices(); // Refresh the full list
    };

    const handleCloseModal = () => {
        setModalState({ isOpen: false, selectedInvoice: null });
    };

    const handleOpenModal = (invoice) => {
        setModalState({ isOpen: true, selectedInvoice: invoice });
    };

    if (user.role !== 'SuperAdmin') {
        return (
            <div>
                <div className="page-header">
                    <h1 className="page-title">Billing</h1>
                </div>
                <div className="card">
                     <p>View your subscription details and invoice history here.</p>
                </div>
            </div>
        );
    }
    
    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">Billing Management (Admin)</h1>
            </div>

            <div className="card">
                {isLoading && <div className="placeholder-text">Loading invoices...</div>}
                {error && <p className="error-message">Error: {error}</p>}
                
                {!isLoading && invoices.length === 0 && (
                    <div className="placeholder-text">No invoices found.</div>
                )}

                {!isLoading && invoices.length > 0 && (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Invoice ID</th>
                                <th>Customer</th>
                                <th>Property</th>
                                <th>Status</th>
                                <th>Date Issued</th>
                                <th>Amount Due</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map(invoice => (
                                <tr key={invoice.id}>
                                    <td data-label="Invoice ID">#{invoice.id}</td>
                                    <td data-label="Customer">{invoice.customer_name}</td>
                                    <td data-label="Property">{invoice.property_name || 'N/A'}</td>
                                    <td data-label="Status">
                                        <span className={`status-badge status-${invoice.status.toLowerCase()}`}>
                                            {invoice.status}
                                        </span>
                                    </td>
                                    <td data-label="Date Issued">{new Date(invoice.created_at).toLocaleDateString()}</td>
                                    <td data-label="Amount Due">${parseFloat(invoice.amount_due).toFixed(2)}</td>
                                    <td data-label="Actions">
                                        {invoice.status === 'OPEN' ? (
                                            <button 
                                                className="button action-button"
                                                onClick={() => handleOpenModal(invoice)}
                                            >
                                                Record Payment
                                            </button>
                                        ) : (
                                            <div className="paid-indicator">
                                                <CheckCircle size={18} />
                                                <span>Paid</span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* --- 2. THE MODAL IS NOW RENDERED CLEANLY --- */}
            <RecordPaymentModal
                isOpen={modalState.isOpen}
                onClose={handleCloseModal}
                onSave={handleRecordPayment}
                invoice={modalState.selectedInvoice}
            />
        </div>
    );
};

export default BillingScreen;